import React from 'react';
import PropTypes from 'prop-types';
import { Link as RouterLink } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import {
  Box,
  Grid,
  Paper,
  Stack,
  Typography,
  Link,
  Chip,
} from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';

 /**
  * AuthShell — shared layout for authentication pages (login / register).
  *
  * Two-column on md+, single-column on small screens:
  *   - Left  : brand panel (dark gradient + tagline + value bullets)
  *   - Right : form panel (centered Paper card with the form content)
  *
  * Mirrors the visual language of the Home page: dark primary panel,
  * secondary-blue accents, Inter typography, soft Paper shadows.
  *
  * `bullets` accepts either:
  *   - a string (fallback, renders a default check icon), or
  *   - { icon: ElementType, text: string } for a custom leading icon.
  */
const AuthShell = ({
  overline,
  title,
  titleAccent,
  subtitle,
  bullets = [],
  trustChip,
  children,
  footer,
}) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        minHeight: { xs: 'auto', md: '100vh' },
        display: 'flex',
        alignItems: 'stretch',
        backgroundColor: 'background.default',
      }}
    >
      <Grid container sx={{ flexGrow: 1, minHeight: '100%' }}>

        {/* ── Brand panel ── */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            position: 'relative',
            display: { xs: 'block', md: 'flex' },
            flexDirection: 'column',
            justifyContent: 'center',
            overflow: 'hidden',
            backgroundColor: 'primary.main',
            backgroundImage: `radial-gradient(circle at 30% 20%, ${theme.palette.secondary.main}33, transparent 60%), radial-gradient(circle at 80% 90%, ${theme.palette.secondary.light}22, transparent 55%)`,
            color: '#FFFFFF',
            px: { xs: 4, md: 8 },
            py: { xs: 4, md: 10 },
            order: { xs: 2, md: 1 },
          }}
        >
          <Box sx={{ maxWidth: 480 }}>
            <Typography
              variant="overline"
              sx={{
                display: 'block',
                mb: 2,
                color: 'inherit',
                opacity: 0.8,
              }}
            >
              ZORT
            </Typography>

            <Typography
              variant="h2"
              sx={{
                color: '#FFFFFF',
                mb: 2,
                fontSize: { xs: '2rem', md: '2.5rem' },
                lineHeight: 1.15,
              }}
            >
              Tus finanzas,{' '}
              <Box
                component="span"
                sx={{ color: theme.palette.secondary.light }}
              >
                bajo control
              </Box>
            </Typography>

            <Typography
              sx={{
                color: 'rgba(255,255,255,0.78)',
                maxWidth: 420,
                mb: { xs: 3, md: 5 },
                fontSize: '1rem',
                lineHeight: 1.6,
              }}
            >
              Importa tus movimientos bancarios automáticamente y descubre
              a dónde se va tu dinero cada mes.
            </Typography>

            {bullets.length > 0 && (
              <Stack
                spacing={2}
                sx={{ display: { xs: 'none', md: 'flex' } }}
              >
                {bullets.map((b) => {
                  const text = typeof b === 'string' ? b : b.text;
                  const Icon =
                    (typeof b === 'object' && b.icon) ||
                    CheckCircleRoundedIcon;
                  return (
                    <Stack
                      key={text}
                      direction="row"
                      spacing={2}
                      alignItems="center"
                    >
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: 'rgba(59, 130, 246, 0.18)',
                          flexShrink: 0,
                        }}
                      >
                        <Icon
                          sx={{
                            color: theme.palette.secondary.light,
                            fontSize: 20,
                          }}
                        />
                      </Box>
                      <Typography
                        sx={{
                          color: 'rgba(255,255,255,0.88)',
                          fontSize: '0.95rem',
                          fontWeight: 500,
                        }}
                      >
                        {text}
                      </Typography>
                    </Stack>
                  );
                })}
              </Stack>
            )}
          </Box>

          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{
              mt: { xs: 3, md: 6 },
              color: 'rgba(255,255,255,0.55)',
              display: { xs: 'none', md: 'flex' },
            }}
          >
            <LockRoundedIcon sx={{ fontSize: 16 }} />
            <Typography variant="caption">
              Conexión cifrada vía HTTPS · Autenticación protegida con JWT
            </Typography>
          </Stack>

          <Typography
            variant="caption"
            sx={{
              display: { xs: 'none', md: 'block' },
              mt: 2,
              color: 'rgba(255,255,255,0.45)',
            }}
          >
            Hecho en Chile 🇨🇱
          </Typography>
        </Grid>

        {/* ── Form panel ── */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: { xs: 3, sm: 6 },
            py: { xs: 4, md: 10 },
            order: { xs: 1, md: 2 },
          }}
        >
          <Box sx={{ width: '100%', maxWidth: 440 }}>
            {trustChip && (
              <Chip
                icon={trustChip.icon}
                label={trustChip.label}
                color="secondary"
                variant="outlined"
                size="small"
                sx={{
                  mb: 3,
                  bgcolor: 'secondary.main',
                  color: '#FFFFFF',
                  borderColor: 'secondary.main',
                  fontWeight: 600,
                  '& .MuiChip-icon': { color: '#FFFFFF', ml: 0.75 },
                }}
              />
            )}

            <Typography
              variant="overline"
              sx={{ display: 'block', mb: 1.5 }}
            >
              {overline}
            </Typography>

            <Typography variant="h3" sx={{ mb: 1 }}>
              {title}{' '}
              {titleAccent && (
                <Box
                  component="span"
                  sx={{ color: 'secondary.main' }}
                >
                  {titleAccent}
                </Box>
              )}
            </Typography>

            {subtitle && (
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ mb: 4, lineHeight: 1.6 }}
              >
                {subtitle}
              </Typography>
            )}

            <Paper
              sx={{
                p: { xs: 3, sm: 4 },
                borderLeft: '4px solid',
                borderLeftColor: 'secondary.main',
              }}
            >
              {children}
            </Paper>

            {footer && (
              <Box sx={{ mt: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  {footer.text}{' '}
                  <Link
                    component={RouterLink}
                    to={footer.to}
                    sx={{ fontWeight: 700 }}
                  >
                    {footer.label}
                  </Link>
                </Typography>
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

const bulletShape = PropTypes.oneOfType([
  PropTypes.string,
  PropTypes.shape({
    icon: PropTypes.elementType,
    text: PropTypes.string.isRequired,
  }),
]);

AuthShell.propTypes = {
  overline: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  titleAccent: PropTypes.node,
  subtitle: PropTypes.string,
  bullets: PropTypes.arrayOf(bulletShape),
  trustChip: PropTypes.shape({
    icon: PropTypes.element,
    label: PropTypes.string.isRequired,
  }),
  children: PropTypes.node.isRequired,
  footer: PropTypes.shape({
    text: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    to: PropTypes.string.isRequired,
  }),
};

export default AuthShell;